"use client"

import { useContext, useEffect, useState } from "react"
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter"
import { SearchFieldCollectionMappings } from "./Constants"
import { getTypesenseKey } from "./getTypesenseKey"
import { TypesenseContext } from "./TypesenseProvider"
import { useUiTypesenseClientProps } from "./useUiTypesenseClient"

interface TypesenseTotalHookReturn {
  totalResults: number
  loading: boolean
  error: string | null
}

export function useTypesenseTotal(
  props: useUiTypesenseClientProps & {
    query?: string
    skipFetch?: boolean
  },
): TypesenseTotalHookReturn {
  const [totalResults, setTotalResults] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { typesenseHost, apiKey, multiCollectionKeysV2 } =
    useContext(TypesenseContext)

  const typesenseKey = getTypesenseKey({
    collectionId: props.collectionId,
    searchApiKey: apiKey,
    multiCollectionKeysV2: multiCollectionKeysV2,
    usePermissions: undefined,
    context: props.context,
  })

  useEffect(() => {
    if (!typesenseKey || !props.collectionId || props.skipFetch) return

    const collectionName = SearchFieldCollectionMappings[props.collectionId]
    if (!collectionName) return

    const fetchTotal = async () => {
      setLoading(true)
      setError(null)

      try {
        const adapter = new TypesenseInstantSearchAdapter({
          server: {
            apiKey: typesenseKey,
            sendApiKeyAsQueryParam: false,
            nodes: [{ url: `${typesenseHost}/search` }],
          },
          additionalSearchParameters: {
            collection: collectionName,
            per_page: 0,
            ...props.searchParams,
          },
        })

        const searchClient = adapter.searchClient
        const result = await searchClient.search([
          {
            indexName: collectionName,
            query: props.query || "*",
            params: { hitsPerPage: 0 },
          },
        ])

        setTotalResults(result.results[0]?.nbHits || 0)
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error")
      } finally {
        setLoading(false)
      }
    }

    fetchTotal()
  }, [typesenseKey, props.collectionId, props.query, props.searchParams])

  return { totalResults, loading, error }
}
